"use client";

import { motion } from "framer-motion";
import { Mic, MessageSquare, BarChart3, ChevronRight, Star } from "lucide-react";

export function ScenarioPickerIllustration() {
  const scenarios = [
    { name: "Cold Call — IT Manager", difficulty: "Medium", color: "bg-blue-500/10 text-blue-400" },
    { name: "Bezwaar — Budget", difficulty: "Moeilijk", color: "bg-amber-500/10 text-amber-400" },
    {
      name: "Closing — Enterprise",
      difficulty: "Expert",
      color: "bg-purple-500/10 text-purple-400",
    },
  ];

  return (
    <motion.div
      className="mt-4 overflow-hidden rounded-lg border border-white/5 bg-[#0a0f1c]"
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <div className="border-b border-white/5 px-3 py-2 text-[10px] font-semibold text-gray-300">
        Kies een scenario
      </div>
      <div className="space-y-1.5 p-2">
        {scenarios.map((s, i) => (
          <div
            key={s.name}
            className={`flex items-center justify-between rounded-md px-3 py-2 text-[10px] transition-colors ${i === 0 ? "border border-blue-500/20 bg-blue-500/5" : "border border-transparent hover:bg-white/[0.02]"}`}
          >
            <div className="flex items-center gap-2">
              <MessageSquare className="h-3 w-3 text-gray-500" />
              <span className="text-gray-300">{s.name}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`rounded-full px-1.5 py-0.5 text-[8px] font-medium ${s.color}`}>
                {s.difficulty}
              </span>
              <ChevronRight className="h-3 w-3 text-gray-600" />
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

export function VoiceSessionIllustration() {
  const barHeights = [
    3, 5, 8, 12, 16, 20, 16, 22, 18, 14, 10, 16, 20, 14, 8, 12, 18, 22, 16, 10, 6, 4,
  ];

  return (
    <motion.div
      className="mt-4 overflow-hidden rounded-lg border border-white/5 bg-[#0a0f1c]"
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.3 }}
    >
      <div className="border-b border-white/5 px-3 py-2 text-[10px] font-semibold text-gray-300">
        Live sessie
      </div>
      <div className="flex flex-col items-center gap-3 p-4">
        {/* Static waveform — no infinite animation */}
        <div className="flex h-10 items-center gap-0.5">
          {barHeights.map((h, i) => (
            <motion.div
              key={i}
              className="w-1 rounded-full bg-blue-500"
              initial={{ height: 4 }}
              whileInView={{ height: h }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.4 + i * 0.03 }}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-500/20">
            <Mic className="h-4 w-4 text-red-400" />
          </div>
          <div className="text-[10px] text-gray-300">02:34 — AI-prospect luistert...</div>
        </div>
        {/* Live coaching hint */}
        <div className="w-full rounded-md border border-emerald-500/10 bg-emerald-500/5 px-3 py-1.5 text-[9px] text-emerald-400">
          Tip: Stel een open vraag om het bezwaar te verkennen
        </div>
      </div>
    </motion.div>
  );
}

export function CoachingReportIllustration() {
  const scores = [
    { label: "Opening", score: 92 },
    { label: "Bezwaren", score: 78 },
    { label: "Closing", score: 85 },
    { label: "Rapport", score: 88 },
  ];

  return (
    <motion.div
      className="mt-4 overflow-hidden rounded-lg border border-white/5 bg-[#0a0f1c]"
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.4 }}
    >
      <div className="flex items-center gap-1.5 border-b border-white/5 px-3 py-2 text-[10px] font-semibold text-gray-300">
        <BarChart3 className="h-3 w-3 text-blue-400" /> Coaching Rapport
      </div>
      <div className="p-3">
        {/* Overall score */}
        <div className="mb-3 flex items-center justify-between">
          <span className="text-[10px] text-gray-300">Totaalscore</span>
          <div className="flex items-center gap-1">
            <span className="text-lg font-bold text-emerald-400">86</span>
            <span className="text-[10px] text-gray-500">/100</span>
            <div className="ml-1 flex gap-0.5">
              {[1, 2, 3, 4].map((s) => (
                <Star key={s} className="h-2.5 w-2.5 fill-amber-400 text-amber-400" />
              ))}
              <Star className="h-2.5 w-2.5 text-gray-600" />
            </div>
          </div>
        </div>
        {/* Score bars */}
        <div className="space-y-2">
          {scores.map((s) => (
            <div key={s.label} className="space-y-0.5">
              <div className="flex justify-between text-[9px]">
                <span className="text-gray-300">{s.label}</span>
                <span className="text-gray-500">{s.score}%</span>
              </div>
              <div className="h-1 rounded-full bg-white/5">
                <motion.div
                  className={`h-full rounded-full ${s.score >= 85 ? "bg-emerald-500" : s.score >= 70 ? "bg-blue-500" : "bg-amber-500"}`}
                  initial={{ width: 0 }}
                  whileInView={{ width: `${s.score}%` }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
